import React from 'react';
import Grid from '@mui/material/Grid';
import {
  Box,
  Chip,
  Container,
  Divider,
  Stack,
  Typography,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
} from '@mui/material';
import {
  Architecture as ArchitectureIcon,
  RocketLaunch as RocketLaunchIcon,
  Security as SecurityIcon,
  Timeline as TimelineIcon,
  Insights as InsightsIcon,
} from '@mui/icons-material';

export const Engineering = (): React.ReactNode => {
  const principles = [
    {
      title: 'Systems Thinking',
      description:
        'Architect solutions that balance delivery speed with observability, resilience, and cost controls.',
      icon: <ArchitectureIcon color='primary' />,
      highlights: [
        'Event-driven patterns',
        'Reliable data flows',
        'API-first design',
      ],
    },
    {
      title: 'Operational Excellence',
      description:
        'Keep services healthy with strong release hygiene, proactive monitoring, and on-call readiness.',
      icon: <TimelineIcon color='primary' />,
      highlights: [
        'SLO-focused dashboards',
        'Progressive delivery',
        'Actionable runbooks',
      ],
    },
    {
      title: 'Security by Default',
      description:
        'Bake in least-privilege access, safe secrets handling, and threat-aware code reviews.',
      icon: <SecurityIcon color='primary' />,
      highlights: [
        'Zero-trust posture',
        'Static & dynamic checks',
        'Dependency governance',
      ],
    },
  ];

  const showcases = [
    {
      title: 'Streaming analytics stack',
      impact:
        'Shipped real-time insights for 50k+ daily users by pairing Kafka ingestion with autoscaled workers and typed contracts.',
      tags: ['Kafka', 'TypeScript', 'Kubernetes', 'OpenAPI'],
    },
    {
      title: 'Resilient payments gateway',
      impact:
        'Reduced charge failures by 18% through idempotent workflows, circuit breakers, and contract testing across providers.',
      tags: ['Node.js', 'PostgreSQL', 'k6', 'PACT'],
    },
    {
      title: 'Developer productivity tooling',
      impact:
        'Accelerated onboarding with golden paths, reusable CI templates, and lint autofixers wired into pre-commit hooks.',
      tags: ['GitHub Actions', 'Lint/Format', 'Templates', 'DX'],
    },
  ];

  const toolbox = [
    'TypeScript',
    'React & MUI',
    'Node.js',
    'PostgreSQL',
    'Kafka',
    'Docker & Kubernetes',
    'Terraform',
    'Grafana & OpenTelemetry',
    'GitHub Actions',
    'Testing: Vitest, React Testing Library, k6',
  ];

  return (
    <Box
      sx={{
        py: { xs: 8, md: 12 },
        background:
          'radial-gradient(circle at 20% 20%, rgba(79, 70, 229, 0.08), transparent 35%), radial-gradient(circle at 80% 0%, rgba(34, 197, 94, 0.12), transparent 30%)',
      }}
    >
      <Container maxWidth='lg'>
        <Stack spacing={4}>
          <Box>
            <Chip label='Engineering Playbook' color='primary' sx={{ mb: 2 }} />
            <Typography variant='h3' sx={{ fontWeight: 800, mb: 1 }}>
              Building reliable, measurable software
            </Typography>
            <Typography
              variant='subtitle1'
              color='text.secondary'
              sx={{ maxWidth: 720 }}
            >
              A snapshot of how I design, deliver, and operate systems so
              product teams can ship fast without sacrificing quality.
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {principles.map(principle => (
              <Grid size={{ xs: 12, md: 4 }} key={principle.title}>
                <Card
                  variant='outlined'
                  sx={{
                    height: '100%',
                    borderColor: 'rgba(148, 163, 184, 0.2)',
                    background:
                      'linear-gradient(180deg, rgba(15, 23, 42, 0.8) 0%, rgba(15, 23, 42, 0.6) 100%)',
                  }}
                >
                  <CardContent>
                    <Stack
                      direction='row'
                      spacing={1.5}
                      alignItems='center'
                      sx={{ mb: 1.5 }}
                    >
                      {principle.icon}
                      <Typography variant='h5' sx={{ fontWeight: 700 }}>
                        {principle.title}
                      </Typography>
                    </Stack>
                    <Typography
                      variant='body1'
                      color='text.secondary'
                      sx={{ mb: 1.5 }}
                    >
                      {principle.description}
                    </Typography>
                    <Stack
                      direction='row'
                      spacing={1}
                      flexWrap='wrap'
                      useFlexGap
                    >
                      {principle.highlights.map(highlight => (
                        <Chip
                          key={highlight}
                          label={highlight}
                          size='small'
                          sx={{ mb: 1 }}
                        />
                      ))}
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Divider flexItem sx={{ borderColor: 'rgba(148, 163, 184, 0.3)' }}>
            <Stack direction='row' spacing={1} alignItems='center'>
              <RocketLaunchIcon fontSize='small' color='primary' />
              <Typography variant='subtitle2'>Selected wins</Typography>
            </Stack>
          </Divider>

          <Grid container spacing={3}>
            {showcases.map(showcase => (
              <Grid size={{ xs: 12, md: 4 }} key={showcase.title}>
                <Card
                  sx={{
                    height: '100%',
                    background:
                      'linear-gradient(135deg, rgba(6, 78, 59, 0.75), rgba(14, 116, 144, 0.5))',
                    color: 'white',
                    boxShadow: '0 24px 50px -30px rgba(0,0,0,0.5)',
                  }}
                >
                  <CardContent>
                    <Stack spacing={1.5}>
                      <Typography variant='h6' sx={{ fontWeight: 800 }}>
                        {showcase.title}
                      </Typography>
                      <Typography variant='body2' sx={{ opacity: 0.9 }}>
                        {showcase.impact}
                      </Typography>
                      <Stack
                        direction='row'
                        spacing={1}
                        flexWrap='wrap'
                        useFlexGap
                      >
                        {showcase.tags.map(tag => (
                          <Chip
                            key={tag}
                            label={tag}
                            size='small'
                            sx={{
                              backgroundColor: 'rgba(255,255,255,0.14)',
                              color: 'white',
                            }}
                          />
                        ))}
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Card
                variant='outlined'
                sx={{ height: '100%', borderColor: 'rgba(148, 163, 184, 0.2)' }}
              >
                <CardContent>
                  <Stack
                    direction='row'
                    spacing={1.5}
                    alignItems='center'
                    sx={{ mb: 1.5 }}
                  >
                    <InsightsIcon color='primary' />
                    <Typography variant='h5' sx={{ fontWeight: 700 }}>
                      Technical toolbox
                    </Typography>
                  </Stack>
                  <Stack direction='row' spacing={1} flexWrap='wrap' useFlexGap>
                    {toolbox.map(item => (
                      <Chip key={item} label={item} sx={{ mb: 1 }} />
                    ))}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Card
                variant='outlined'
                sx={{
                  height: '100%',
                  borderColor: 'rgba(148, 163, 184, 0.2)',
                  background:
                    'linear-gradient(180deg, rgba(51, 65, 85, 0.7), rgba(15, 23, 42, 0.85))',
                }}
              >
                <CardContent>
                  <Stack
                    direction='row'
                    spacing={1.5}
                    alignItems='center'
                    sx={{ mb: 1.5 }}
                  >
                    <SecurityIcon color='primary' />
                    <Typography variant='h5' sx={{ fontWeight: 700 }}>
                      Delivery checklist
                    </Typography>
                  </Stack>
                  <List dense>
                    {[
                      'Define clear SLIs/SLOs with dashboards before cutting traffic.',
                      'Automate static analysis, tests, and preview deploys per PR.',
                      'Use feature flags and gradual rollouts for low-risk launches.',
                      'Instrument traces and logs to follow user journeys end-to-end.',
                      'Document decisions concisely so teammates move faster.',
                    ].map(item => (
                      <ListItem key={item} sx={{ px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <TimelineIcon fontSize='small' color='primary' />
                        </ListItemIcon>
                        <Typography variant='body2'>{item}</Typography>
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Stack>
      </Container>
    </Box>
  );
};
